import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, func, number, shape, string } from 'prop-types'
import { PureComponent } from 'react'
import { connectRange } from 'react-instantsearch-dom'

import {
  FILTERS_DEBOUNCE_TIME,
  PRODUCT_FILTER_RESET,
} from 'shared/experiments/components/NewSerp/consts'
import FlyoutFilter from 'shared/experiments/components/NewSerp/FlyoutFilter/FlyoutFilter'
import {
  getMiddleValue,
  withSearchStateHandler,
} from 'shared/experiments/components/NewSerp/helpers'
import InputRange from 'shared/experiments/components/NewSerp/RangeFilter/InputRange'
import { subscribe } from 'shared/store'
import { normalizeRangeInputValue } from 'shared/utils/algolia/filters'
import type { InferPropsWithDefaults } from 'shared/utils/propTypes'
import type { SearchRange } from 'types/search'
import IconBin from 'views/components/atoms/Icon/IconBin'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import debounce from 'views/utils/debounce'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

/**
 * When `defaultValues` prop provided, it overrides the range min and max
 * that received from the connected props of the component.
 * Otherwise it gets a min and max for all the hits in the matching records set.
 */
class RangeFilter extends PureComponent<ComponentProps, ComponentState> {
  subscriptions: CallableFunction[] = []
  state = this.getInitialState()

  // The method debounces calls to save the requests amount
  refine: CallableFunction

  constructor(props: ComponentProps) {
    super(props)

    this.refine = debounce(this.refineWorker, FILTERS_DEBOUNCE_TIME)

    this.subscriptions.push(
      subscribe.after(PRODUCT_FILTER_RESET, this.handleResetClick)
    )
  }

  getInitialState(): ComponentState {
    const {
      min,
      max,
      currentRefinement,
      defaultValues,
      rangeValues,
      tenant,
      attribute,
    } = this.props
    const { min: defaultMin, max: defaultMax } = defaultValues
    const { min: rMin, max: rMax } = rangeValues
    let rangeMin
    let rangeMax
    let values

    if (attribute === 'price') {
      const normalizedMin = min / 100
      const normalizedMax = max / 100

      rangeMin = rMin ?? normalizedMin
      rangeMax = rMax ?? normalizedMax

      values = [
        defaultMin ? defaultMin : normalizedMin,
        defaultMax ? defaultMax : normalizedMax,
      ]
    } else {
      rangeMin = rMin ?? min
      rangeMax = rMax ?? max

      values = [defaultMin ? defaultMin : min, defaultMax ? defaultMax : max]
    }

    const isAdjusted = rangeMin !== values[0] || rangeMax !== values[1]

    return {
      rangeMin,
      rangeMax,
      values,
      currentRefinement,
      isAdjusted,
      tenant,
      noResults: false,
      applied: false,
    } as ComponentState
  }

  componentDidUpdate(prevProps: Readonly<ComponentProps>) {
    const {
      rangeValues: { min: prevMin, max: prevMax },
    } = prevProps
    const {
      rangeValues: { min: rangeMin, max: rangeMax },
    } = this.props

    if (prevMin !== rangeMin || prevMax !== rangeMax) {
      const {
        values: [min, max],
        applied,
      } = this.state

      let newMin = rangeMin
      let newMax = rangeMax

      if (applied) {
        newMin = getMiddleValue(rangeMin, min, rangeMax)
        newMax = getMiddleValue(rangeMin, max, rangeMax)
      }

      if (newMin < newMax) {
        this.setState({
          values: [newMin, newMax],
          rangeMin,
          rangeMax,
        })
      }
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(unsub => unsub())
  }

  /**
   * Component uses debounced call via `this.refine`
   */
  refineWorker = ({ min, max }: SearchRange) => {
    const { currentRefinement } = this.state
    const { refine, attribute } = this.props
    const minNumber = Number(min)
    const maxNumber = Number(max)

    // Try to refine: fails when min/max is out of boundaries
    try {
      if (!isNaN(minNumber) && !isNaN(maxNumber)) {
        withSearchStateHandler(() => {
          refine({
            ...currentRefinement,
            min: attribute === 'price' ? minNumber * 100 : minNumber,
            max: attribute === 'price' ? maxNumber * 100 : maxNumber,
          })
        })
      }
      // eslint-disable-next-line no-empty
    } catch (_) {}
  }

  isValidValue(newValue: number, handleMinInput: boolean): boolean {
    const {
      values: [min, max],
    } = this.state

    return handleMinInput ? newValue <= max : min <= newValue
  }

  updateValues({
    event,
    handleMinInput = false,
    refine = false,
  }: {
    event: MouseEvent | KeyboardEvent
    handleMinInput?: boolean
    refine?: boolean
  }) {
    event.preventDefault()
    event.stopPropagation()

    const inputElement = event.target as HTMLInputElement
    const newValue = normalizeRangeInputValue(inputElement.value)

    // Update the input whatever was typed in,
    // but refine only when the value is in valid range and requested for
    this.setState(
      prevState => {
        const [prevMin, prevMax] = prevState.values
        const values = handleMinInput
          ? [newValue, prevMax]
          : [prevMin, newValue]

        return {
          values,
          isAdjusted: true,
        }
      },
      () => {
        if (this.isValidValue(newValue, handleMinInput) && refine) {
          withSearchStateHandler(this.handleApply)
        }
      }
    )
  }

  getNormalizedValues(attribute: string): number[] {
    const { values } = this.state

    if (attribute === 'price') {
      return values.map(value => parseFloat(value.toFixed(2)))
    }

    return values
  }

  hasRefinedValues(): boolean {
    const {
      currentRefinement: { max, min },
      max: rangeMax,
      min: rangeMin,
    } = this.props

    return min !== rangeMin || max !== rangeMax
  }

  handleApply = (event?: Event) => {
    if (event) {
      event.preventDefault()
    }

    const {
      rangeMin,
      rangeMax,
      values: [min, max],
    } = this.state

    const finalMinValue = getMiddleValue(rangeMin, min, rangeMax)
    const finalMaxValue = getMiddleValue(rangeMin, max, rangeMax)

    if (finalMaxValue === rangeMin || finalMinValue === rangeMax) {
      this.refine({ min: rangeMax, max: 0 })
    } else if (finalMaxValue >= finalMinValue) {
      this.refine({ min: finalMinValue, max: finalMaxValue })
    }

    this.setState({ values: [finalMinValue, finalMaxValue], applied: true })
  }

  handleResetClick = () => {
    const { rangeMin, rangeMax } = this.state

    this.setState(
      {
        values: [rangeMin, rangeMax],
        applied: false,
        isAdjusted: false,
      },
      () => withSearchStateHandler(() => this.props.refine({}))
    )
  }

  handleMinChange = (event: KeyboardEvent | MouseEvent) => {
    this.updateValues({ event, handleMinInput: true })
  }

  handleMaxChange = (event: KeyboardEvent | MouseEvent) => {
    this.updateValues({ event })
  }

  handleMinKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.updateValues({ event, handleMinInput: true, refine: true })
    }
  }

  handleMaxKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.updateValues({ event, refine: true })
    }
  }

  handleRheostatUpdate = ({ values: [min, max] }: { values: number[] }) => {
    const {
      values: [prevMin, prevMax],
    } = this.state
    const { attribute } = this.props

    if (prevMin === min && prevMax === max) {
      return
    }

    const newValues =
      attribute === 'price' ? [min, max] : [Math.round(min), Math.round(max)]

    this.setState({ values: newValues, isAdjusted: true }, () => {
      withSearchStateHandler(this.handleApply)
    })
  }

  render() {
    const { attribute, label, ...rest } = this.props
    const { rangeMin, rangeMax, isAdjusted, tenant } = this.state
    const values = this.getNormalizedValues(attribute)
    const isRefined = this.hasRefinedValues()

    return (
      <FlyoutFilter
        attribute={attribute}
        label={label}
        active={isRefined}
        count={isRefined ? 1 : 0}
        tenant={tenant}
      >
        <div className="u-no-border-radius u-padding@small-tiny m-PriceRange">
          <InputRange
            {...rest}
            events={{
              onMaxChange: this.handleMaxChange,
              onMaxKeyUp: this.handleMaxKeyUp,
              onMinChange: this.handleMinChange,
              onMinKeyUp: this.handleMinKeyUp,
              onRheostatUpdate: this.handleRheostatUpdate,
            }}
            delimiterLabel="-"
            values={values}
            range={[rangeMin, rangeMax]}
            decimalPlaces={attribute === 'price' ? 2 : 0}
            hideUpdateButton
            rheostatClassName="m-InputRange__rheostatPrice"
            noPadding
          />
          <div className="u-margin-large--top u-padding--top u-border--top e-RefinementList__PriceRange__resetButton--wrapper">
            <ButtonAsLink
              className={classNames('o-RefinementList__resetButton', {
                'u-font-weight--bold': isAdjusted,
              })}
              onClick={this.handleResetClick}
              disabled={!isAdjusted}
            >
              <TextWithIcon icon={<IconBin size="small" />}>
                <Trans id="searchResults.label.clearFilter" />
              </TextWithIcon>
            </ButtonAsLink>
          </div>
        </div>
      </FlyoutFilter>
    )
  }
}

const propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  name: string.isRequired,
  values: arrayOf(number).isRequired,
  rangeValues: shape({ min: number.isRequired, max: number.isRequired })
    .isRequired,
  defaultValues: shape({ min: number.isRequired, max: number.isRequired })
    .isRequired,
  attribute: string.isRequired,
  currentRefinement: shape({ min: number.isRequired, max: number.isRequired })
    .isRequired,
  refine: func.isRequired,
  label: string.isRequired,
  min: number.isRequired,
  max: number.isRequired,
  tenant: string,
}

const defaultProps = {
  //@ts-ignore
  ...InputRange.defaultProps,
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = {
  rangeMin: number
  rangeMax: number
  values: number[]
  currentRefinement: SearchRange
  isAdjusted: boolean
  noResults: boolean
  applied: boolean
  tenant: string
}

export default connectRange(withShopAndRuntimeConfig(RangeFilter))
